import { useMutation, useQuery } from '@tanstack/react-query';
import { apiClient } from './client';
import type {
  AgcStorageResponse,
  AgcStorageReviewStrategiesResponse,
  AgcStorageReviewStrategy,
  AgcStorageReviewStrategyType,
  AgcStorageReviewStrategyStatus,
  AgcStorageTheoreticalOptimalResponse,
} from '@/types/api';

export interface AgcStorageParams {
  station: string;
  date: string;
  penalty_base: number;
  cap_max: number;
  eff: number;
  curt_thres: number;
  include_attribution?: boolean;
  use_temp_price_fallback?: boolean;
}

export function useAgcStorage() {
  return useMutation({
    mutationFn: async (params: AgcStorageParams) => {
      const res = await apiClient.get<AgcStorageResponse>('/agc-storage/data', {
        params: {
          station: params.station,
          date: params.date,
          penalty_base: params.penalty_base,
          cap_max: params.cap_max,
          eff: params.eff,
          curt_thres: params.curt_thres,
          include_attribution: params.include_attribution,
          use_temp_price_fallback: params.use_temp_price_fallback,
        },
      });
      return res.data;
    },
  });
}

export interface AgcStorageTheoreticalOptimalParams {
  station: string;
  date: string;
}

export function useAgcStorageTheoreticalOptimal() {
  return useMutation({
    mutationFn: async (params: AgcStorageTheoreticalOptimalParams) => {
      const res = await apiClient.get<AgcStorageTheoreticalOptimalResponse>('/agc-storage/theoretical-optimal', {
        params: {
          station: params.station,
          date: params.date,
        },
      });
      return res.data;
    },
  });
}

export function useAgcStorageReviewTheoreticalOptimal() {
  return useMutation({
    mutationFn: async (params: AgcStorageTheoreticalOptimalParams) => {
      const res = await apiClient.get<AgcStorageTheoreticalOptimalResponse>('/agc-storage/review-theoretical-optimal', {
        params: {
          station: params.station,
          date: params.date,
        },
      });
      return res.data;
    },
  });
}

export interface AgcStorageReviewStrategiesParams {
  station: string;
  date: string;
}

export interface AgcStorageReviewStrategyPayload {
  strategy_type: AgcStorageReviewStrategyType;
  strategy_name?: string | null;
  initial_soc_mwh?: number | null;
  actions?: Array<Record<string, unknown>>;
  carryover_soc?: unknown;
  raw_text: string;
  uncertainties?: string[];
  status?: AgcStorageReviewStrategyStatus;
  source?: Record<string, unknown> | null;
}

export function useAgcStorageReviewStrategies(params: AgcStorageReviewStrategiesParams) {
  return useQuery({
    queryKey: ['agc-storage-review-strategies', params.station, params.date],
    enabled: Boolean(params.station && params.date),
    queryFn: async () => {
      const res = await apiClient.get<AgcStorageReviewStrategiesResponse>('/agc-storage/review-strategies', {
        params: {
          station: params.station,
          date: params.date,
        },
      });
      return res.data;
    },
  });
}

export function useCreateAgcStorageReviewStrategy() {
  return useMutation({
    mutationFn: async (params: AgcStorageReviewStrategiesParams & { payload: AgcStorageReviewStrategyPayload }) => {
      const res = await apiClient.post<{ data: { strategy: AgcStorageReviewStrategy } }>(
        '/agc-storage/review-strategies',
        params.payload,
        {
          params: {
            station: params.station,
            date: params.date,
          },
        }
      );
      return res.data;
    },
  });
}
